import {
  collection,
  doc,
  documentId,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { functionsClient } from "./firebase";
import type { PublicIdentitySnapshot } from "../types/identity";

const db = getFirestore();

export type IdentityGroupRole = "owner" | "admin" | "member";

export type IdentityGroupMembership = {
  groupId: string;
  groupName: string;
  role: IdentityGroupRole;
  isPublic: boolean;
  joinedAt?: unknown;
  updatedAt?: unknown;
};

export type IdentityGroupMember = {
  identityId: string;
  role: IdentityGroupRole;
  joinedAt?: unknown;
  updatedAt?: unknown;
};

export type IdentityGroupConversation = {
  id: string;
  groupId: string;
  whoCanPost: "all_members" | "admins_only";
  lastMessageText: string;
  lastMessageAt: number;
  lastMessageSenderIdentityId: string | null;
  lastMessageSenderSnapshot?: PublicIdentitySnapshot | null;
  createdByIdentityId: string;
  createdAt: number;
  updatedAt: number;
};

export type IdentityGroupInboxThread = {
  groupId: string;
  groupName: string;
  role: IdentityGroupRole;
  lastMessageText: string;
  lastMessageAt: any;
  lastMessageSenderIdentityId: string | null;
  lastMessageSenderSnapshot?: PublicIdentitySnapshot | null;
  lastReadAt?: any;
  hiddenAt?: any;
  updatedAt?: any;
};

export type IdentityGroupMessage = {
  id: string;
  senderIdentityId: string;
  senderSnapshot: PublicIdentitySnapshot | null;
  type: "text";
  text: string;
  createdAt: any;
};

function timestampMillis(value: any) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1_000_000);
  }
  return 0;
}

export function isIdentityGroupUnread(
  thread: Pick<IdentityGroupInboxThread, "lastMessageAt" | "lastMessageSenderIdentityId" | "lastReadAt">,
  identityId: string
) {
  const lastMessageAt = timestampMillis(thread.lastMessageAt);
  return lastMessageAt > 0
    && thread.lastMessageSenderIdentityId !== identityId
    && lastMessageAt > timestampMillis(thread.lastReadAt);
}

const createGroupCallable = httpsCallable(functionsClient, "createIdentityGroup");
const joinGroupCallable = httpsCallable(functionsClient, "joinIdentityGroup");
const requestJoinCallable = httpsCallable(functionsClient, "requestIdentityGroupJoin");
const cancelJoinCallable = httpsCallable(functionsClient, "cancelIdentityGroupJoin");
const approveJoinCallable = httpsCallable(functionsClient, "approveIdentityGroupJoin");
const rejectJoinCallable = httpsCallable(functionsClient, "rejectIdentityGroupJoin");
const leaveGroupCallable = httpsCallable(functionsClient, "leaveIdentityGroup");
const removeMemberCallable = httpsCallable(functionsClient, "removeIdentityGroupMember");
const setMemberRoleCallable = httpsCallable(functionsClient, "setIdentityGroupMemberRole");
const updateGroupCallable = httpsCallable(functionsClient, "updateIdentityGroup");
const archiveGroupCallable = httpsCallable(functionsClient, "archiveIdentityGroup");
const ensureConversationCallable = httpsCallable(functionsClient, "ensureIdentityGroupConversation");
const setPostingModeCallable = httpsCallable(functionsClient, "setIdentityGroupPostingMode");
const sendMessageCallable = httpsCallable(functionsClient, "sendIdentityGroupMessage");
const markReadCallable = httpsCallable(functionsClient, "markIdentityGroupRead");
const listMessagesCallable = httpsCallable(functionsClient, "listIdentityGroupMessages");

export async function createIdentityGroup(input: {
  identityId: string;
  name: string;
  description?: string;
  subject: string;
  isPublic: boolean;
}) {
  return (await createGroupCallable(input)).data as { groupId: string };
}

export async function joinIdentityGroup(identityId: string, groupId: string) {
  return (await joinGroupCallable({ identityId, groupId })).data;
}

export async function requestIdentityGroupJoin(identityId: string, groupId: string) {
  return (await requestJoinCallable({ identityId, groupId })).data;
}

export async function cancelIdentityGroupJoin(identityId: string, groupId: string) {
  return (await cancelJoinCallable({ identityId, groupId })).data;
}

export async function approveIdentityGroupJoin(actorIdentityId: string, groupId: string, targetIdentityId: string) {
  return (await approveJoinCallable({ actorIdentityId, groupId, targetIdentityId })).data;
}

export async function rejectIdentityGroupJoin(actorIdentityId: string, groupId: string, targetIdentityId: string) {
  return (await rejectJoinCallable({ actorIdentityId, groupId, targetIdentityId })).data;
}

export async function leaveIdentityGroup(identityId: string, groupId: string) {
  return (await leaveGroupCallable({ identityId, groupId })).data;
}

export async function removeIdentityGroupMember(actorIdentityId: string, groupId: string, targetIdentityId: string) {
  return (await removeMemberCallable({ actorIdentityId, groupId, targetIdentityId })).data;
}

export async function setIdentityGroupMemberRole(
  actorIdentityId: string,
  groupId: string,
  targetIdentityId: string,
  role: "admin" | "member"
) {
  return (await setMemberRoleCallable({ actorIdentityId, groupId, targetIdentityId, role })).data;
}

export async function updateIdentityGroup(input: {
  actorIdentityId: string;
  groupId: string;
  name: string;
  description?: string;
  subject: string;
  isPublic: boolean;
}) {
  return (await updateGroupCallable(input)).data;
}

export async function archiveIdentityGroup(actorIdentityId: string, groupId: string) {
  return (await archiveGroupCallable({ actorIdentityId, groupId })).data;
}

export async function ensureIdentityGroupConversation(actorIdentityId: string, groupId: string) {
  return (await ensureConversationCallable({ actorIdentityId, groupId })).data as { groupId: string };
}

export async function setIdentityGroupPostingMode(
  actorIdentityId: string,
  groupId: string,
  whoCanPost: "all_members" | "admins_only"
) {
  return (await setPostingModeCallable({ actorIdentityId, groupId, whoCanPost })).data;
}

export async function sendIdentityGroupMessage(input: {
  groupId: string;
  senderIdentityId: string;
  messageId: string;
  text: string;
}) {
  return (await sendMessageCallable(input)).data;
}

export async function markIdentityGroupRead(identityId: string, groupId: string) {
  return (await markReadCallable({ identityId, groupId })).data;
}

export async function listIdentityGroupMessages(identityId: string, groupId: string) {
  return (await listMessagesCallable({ identityId, groupId })).data as {
    conversation: IdentityGroupConversation;
    messages: IdentityGroupMessage[];
  };
}

export async function fetchIdentityGroupMemberships(identityId: string, max = 100) {
  if (!identityId) return [];
  const snap = await getDocs(
    query(collection(db, "identityGroupMemberships", identityId, "groups"), limit(max))
  );
  const items = snap.docs.map((item) => ({
    groupId: item.id,
    ...(item.data() as Omit<IdentityGroupMembership, "groupId">),
  })) as IdentityGroupMembership[];
  return items.sort((a, b) => a.groupName.localeCompare(b.groupName));
}

export function subscribeIdentityGroupMemberships(
  identityId: string,
  onNext: (items: IdentityGroupMembership[]) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    query(collection(db, "identityGroupMemberships", identityId, "groups"), limit(100)),
    (snap) => {
      const items = snap.docs
        .map((item) => ({
          groupId: item.id,
          ...(item.data() as Omit<IdentityGroupMembership, "groupId">),
        }))
        .sort((a, b) => a.groupName.localeCompare(b.groupName));
      onNext(items);
    },
    onError
  );
}

export function subscribeIdentityGroupInbox(
  identityId: string,
  onNext: (threads: IdentityGroupInboxThread[]) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    query(
      collection(db, "identityGroupInboxes", identityId, "threads"),
      orderBy("updatedAt", "desc"),
      limit(100)
    ),
    (snap) => {
      onNext(
        snap.docs
          .map((item) => ({ groupId: item.id, ...(item.data() as any) } as IdentityGroupInboxThread))
          .filter((item) => !item.hiddenAt)
      );
    },
    onError
  );
}

export function subscribeIdentityGroupConversation(
  groupId: string,
  onNext: (conversation: IdentityGroupConversation | null) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    doc(db, "groupConversations", groupId),
    (snap) => {
      if (!snap.exists()) {
        onNext(null);
        return;
      }
      const data = snap.data() as any;
      onNext({
        id: snap.id,
        groupId: data.groupId || snap.id,
        whoCanPost: data.whoCanPost === "admins_only" ? "admins_only" : "all_members",
        lastMessageText: data.lastMessageText || "",
        lastMessageAt: data.lastMessageAt?.toMillis?.() || 0,
        lastMessageSenderIdentityId: data.lastMessageSenderIdentityId || null,
        lastMessageSenderSnapshot: data.lastMessageSenderSnapshot || null,
        createdByIdentityId: data.createdByIdentityId || "",
        createdAt: data.createdAt?.toMillis?.() || 0,
        updatedAt: data.updatedAt?.toMillis?.() || 0,
      });
    },
    onError
  );
}

export function subscribeIdentityGroupMessages(
  groupId: string,
  onNext: (messages: IdentityGroupMessage[]) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    query(
      collection(db, "groupConversations", groupId, "messages"),
      orderBy("createdAt", "desc"),
      limit(100)
    ),
    (snap) => {
      const messages = snap.docs.map((item) => ({
        id: item.id,
        ...(item.data() as any),
      })) as IdentityGroupMessage[];
      onNext(messages.reverse());
    },
    onError
  );
}

export function subscribeIdentityGroupMembers(
  groupId: string,
  onNext: (members: IdentityGroupMember[]) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    query(collection(db, "groups", groupId, "members"), orderBy("joinedAt", "asc")),
    (snap) => {
      onNext(
        snap.docs.map((item) => ({
          identityId: item.id,
          role:
            (item.data() as any)?.role === "owner"
              ? "owner"
              : (item.data() as any)?.role === "admin"
              ? "admin"
              : "member",
          joinedAt: (item.data() as any)?.joinedAt,
          updatedAt: (item.data() as any)?.updatedAt,
        }))
      );
    },
    onError
  );
}

export function subscribeIdentityGroupJoinRequests(
  groupId: string,
  onNext: (identityIds: string[]) => void,
  onError?: (error: any) => void
) {
  return onSnapshot(
    query(collection(db, "groups", groupId, "joinRequests"), orderBy("createdAt", "asc")),
    (snap) => onNext(snap.docs.map((item) => item.id)),
    onError
  );
}

export async function getPublicAccountsByIds(identityIds: string[]) {
  const unique = Array.from(new Set(identityIds.filter(Boolean)));
  if (!unique.length) return {};
  const chunks: string[][] = [];
  for (let index = 0; index < unique.length; index += 10) chunks.push(unique.slice(index, index + 10));
  const results = await Promise.all(
    chunks.map((part) =>
      getDocs(query(collection(db, "accounts"), where(documentId(), "in", part)))
    )
  );
  const map: Record<string, any> = {};
  for (const snap of results) {
    snap.forEach((item) => {
      const data = item.data() as any;
      map[item.id] = {
        identityId: item.id,
        accountId: item.id,
        displayName: data.displayName || "",
        username: data.username || "",
        photoURL: data.photoURL || "",
        accountType: data.accountType,
        verificationStatus: data.verificationStatus,
        entitlements: data.entitlements,
        badges: Array.isArray(data.badges) ? data.badges : [],
        track: data.track,
        mainSubject: data.mainSubject,
      };
    });
  }
  return map;
}
